import type { JsonValue } from './types';

const DECIMAL = /^-?(0|[1-9][0-9]*)(\.[0-9]*[1-9])?$/;
const ISO_DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

/** null/undefined, a blank string or an empty array. `false` is a value. */
export function isEmpty(v: JsonValue | undefined): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Canonical decimal string: no leading zeros, no trailing fractional zeros, "." separator. */
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** An AUTOCOMPLETE value: the id of a record (UUID, any letter case). */
export function isUuid(s: unknown): s is string {
  return typeof s === 'string' && UUID.test(s);
}

export function isDecimal(s: unknown): s is string {
  return typeof s === 'string' && DECIMAL.test(s) && s !== '-0';
}

export function fractionDigits(s: string): number {
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
}

/** Exact comparison of two canonical decimal strings (no floating point). */
export function compareDecimal(a: string, b: string): number {
  const negA = a.startsWith('-');
  const negB = b.startsWith('-');
  if (negA !== negB) return negA ? -1 : 1;
  const sign = negA ? -1 : 1;
  const [ia = '', fa = ''] = (negA ? a.slice(1) : a).split('.');
  const [ib = '', fb = ''] = (negB ? b.slice(1) : b).split('.');
  if (ia.length !== ib.length) return sign * (ia.length < ib.length ? -1 : 1);
  if (ia !== ib) return sign * (ia < ib ? -1 : 1);
  const len = Math.max(fa.length, fb.length);
  const pa = fa.padEnd(len, '0');
  const pb = fb.padEnd(len, '0');
  if (pa === pb) return 0;
  return sign * (pa < pb ? -1 : 1);
}

/** A real calendar date in ISO format, or null. */
export function isoDate(s: unknown): string | null {
  if (typeof s !== 'string' || !ISO_DATE.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m! - 1 || date.getUTCDate() !== d) return null;
  return s;
}

export function deepEqual(a: JsonValue | undefined, b: JsonValue | undefined): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}
